/**
 * @fileoverview Task meta pills (MOR-696)
 *
 * Compact inline indicators shown on calendar event cards to distinguish
 * tasks/milestones from plain events and surface task context at a glance:
 * the kind, priority, a "ghost" planned-date marker, and subtask progress.
 *
 * Reads the Morgan-specific `IEvent.meta` payload threaded through by
 * `eventAdapter.ts`.
 */

import { format, parseISO } from "date-fns";
import { Diamond, ListTodo } from "lucide-react";
import type { IEvent } from "@/calendar/interfaces";
import { priorityMeta } from "@/calendar/task-priority";
import { Badge } from "@/ui/primitives/badge";
import { cn } from "@/utils/misc";

function shortDate(iso: string): string {
  try {
    return format(parseISO(iso), "d MMM");
  } catch {
    return iso;
  }
}

export function TaskMetaPills({
  meta,
  className,
}: {
  meta: IEvent["meta"];
  className?: string;
}) {
  if (!meta) return null;

  const showKind = meta.kind === "task" || meta.kind === "milestone";
  const showStatus = !!meta.statusName;
  const showPriority = !!meta.priority && meta.priority > 0;
  const showSubtasks = meta.subtaskTotal > 0;

  if (
    !showKind &&
    !showStatus &&
    !showPriority &&
    !meta.plannedDate &&
    !showSubtasks
  )
    return null;

  const priority = priorityMeta(meta.priority);
  const PriorityIcon = priority.icon;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-1 text-[10px] opacity-80",
        className,
      )}
    >
      {showStatus && (
        <Badge
          color={meta.statusColor ?? "gray"}
          variant="soft"
          className="px-1 py-0 text-[10px]"
        >
          {meta.statusName}
        </Badge>
      )}

      {showKind && (
        <span className="inline-flex items-center gap-0.5 font-medium">
          {meta.kind === "milestone" ? (
            <Diamond className="size-3" />
          ) : (
            <ListTodo className="size-3" />
          )}
          {meta.kind === "milestone" ? "Milestone" : "Task"}
        </span>
      )}

      {showPriority && (
        <span
          className="inline-flex items-center gap-0.5 font-medium"
          title={priority.label}
        >
          <PriorityIcon className="size-3" />
          {priority.label}
        </span>
      )}

      {meta.plannedDate && (
        <span
          className="inline-flex items-center rounded border border-dashed border-foreground/30 px-1 text-foreground/60"
          title={`Planned ${meta.plannedDate}`}
        >
          Planned {shortDate(meta.plannedDate)}
        </span>
      )}

      {showSubtasks && (
        <span className="inline-flex items-center tabular-nums">
          {meta.subtaskCompleted}/{meta.subtaskTotal}
        </span>
      )}
    </div>
  );
}
